import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bookmark_service,
  favourite_product,
  fetch_favourite_products,
  unfavourite_product,
  favourite_vendor,
  fetch_bookmarked_services,
  fetch_favourite_vendors,
  unbookmark_service,
  unfavourite_vendor,
  type BookmarkedService,
  type FavouriteProduct,
  type FavouriteVendor,
} from './api';
import type { ShopVertical } from '@/features/shop/types';

const FAVOURITE_VENDORS_KEY = ['user', 'favourites', 'vendors'] as const;
const BOOKMARKED_SERVICES_KEY = ['user', 'favourites', 'services'] as const;
const FAVOURITE_PRODUCTS_KEY = ['user', 'favourites', 'products'] as const;

/** A toggle resolves to either the created row or `{ removed }` — callers don't use the payload. */
type ToggleResult = FavouriteVendor | BookmarkedService | FavouriteProduct | { removed: boolean };

export function use_favourite_vendors() {
  return useQuery({ queryKey: FAVOURITE_VENDORS_KEY, queryFn: fetch_favourite_vendors, retry: 0 });
}

export function use_bookmarked_services() {
  return useQuery({ queryKey: BOOKMARKED_SERVICES_KEY, queryFn: fetch_bookmarked_services, retry: 0 });
}

/**
 * Toggle a vendor favourite.
 *
 * Optimistic on removal: a heart that waits for a round-trip before emptying feels broken. The
 * endpoints are idempotent, so a replayed or double-fired toggle is harmless. On error we roll
 * the list back and let the refetch settle the truth.
 */
export function use_toggle_favourite_vendor() {
  const qc = useQueryClient();
  return useMutation<
    ToggleResult,
    unknown,
    { vendor_id: string; favourited: boolean },
    { previous: FavouriteVendor[] | undefined }
  >({
    mutationFn: ({ vendor_id, favourited }) =>
      favourited ? unfavourite_vendor(vendor_id) : favourite_vendor(vendor_id),
    onMutate: async ({ vendor_id, favourited }) => {
      await qc.cancelQueries({ queryKey: FAVOURITE_VENDORS_KEY });
      const previous = qc.getQueryData<FavouriteVendor[]>(FAVOURITE_VENDORS_KEY);
      if (favourited && previous) {
        qc.setQueryData<FavouriteVendor[]>(
          FAVOURITE_VENDORS_KEY,
          previous.filter((f) => f.vendor.id !== vendor_id),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(FAVOURITE_VENDORS_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: FAVOURITE_VENDORS_KEY }),
  });
}

export function use_toggle_bookmark_service() {
  const qc = useQueryClient();
  return useMutation<ToggleResult, unknown, { service_id: string; bookmarked: boolean }>({
    mutationFn: ({ service_id, bookmarked }) =>
      bookmarked ? unbookmark_service(service_id) : bookmark_service(service_id),
    onSettled: () => qc.invalidateQueries({ queryKey: BOOKMARKED_SERVICES_KEY }),
  });
}

export function use_favourite_products() {
  return useQuery({ queryKey: FAVOURITE_PRODUCTS_KEY, queryFn: fetch_favourite_products, retry: 0 });
}

/**
 * Toggle a product favourite.
 *
 * Optimistic on removal for the same reason as the vendor heart: a heart that waits for a
 * round-trip before emptying feels broken, and both endpoints are idempotent so a replayed
 * toggle is harmless.
 *
 * Keyed by (vertical, product_id) because food and pharmacy ids are only unique WITHIN a
 * vertical — filtering the cached list on product_id alone could drop the wrong row.
 */
export function use_toggle_favourite_product() {
  const qc = useQueryClient();
  return useMutation<
    ToggleResult,
    unknown,
    { vertical: ShopVertical; product_id: string; favourited: boolean },
    { previous: FavouriteProduct[] | undefined }
  >({
    mutationFn: ({ vertical, product_id, favourited }) =>
      favourited ? unfavourite_product(vertical, product_id) : favourite_product(vertical, product_id),
    onMutate: async ({ vertical, product_id, favourited }) => {
      await qc.cancelQueries({ queryKey: FAVOURITE_PRODUCTS_KEY });
      const previous = qc.getQueryData<FavouriteProduct[]>(FAVOURITE_PRODUCTS_KEY);
      if (favourited && previous) {
        qc.setQueryData<FavouriteProduct[]>(
          FAVOURITE_PRODUCTS_KEY,
          previous.filter(
            (f) => !(f.product_id === product_id && f.vertical === vertical.toUpperCase()),
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(FAVOURITE_PRODUCTS_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: FAVOURITE_PRODUCTS_KEY }),
  });
}
